import { Braces, Plus, Search, Trash2 } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { useBuilderStore } from "@/lib/builder/builder-store";
import { getMaterial } from "@/lib/builder/materials";
import { filterPropertyPaneConfig, hasEventBinder } from "@/lib/builder/widgets";
import { cn } from "@/lib/utils";
import type {
  ActionConfig,
  EventBinding,
  EventSchema,
  PropertyPaneControl,
} from "@shared/types";

const BINDING_HINT = "支持 {{user.name}}、{{data.customers.0.name}}、{{params.id}}";
const BINDING_RE = /\{\{.*?\}\}/;

function isBindingValue(value: unknown): boolean {
  return typeof value === "string" && BINDING_RE.test(value);
}

function BindingBadge({ value }: { value: unknown }) {
  if (!isBindingValue(value)) return null;
  return (
    <Badge variant="secondary" className="gap-1 text-[10px]">
      <Braces className="h-3 w-3" />
      绑定
    </Badge>
  );
}

function DataBindingField({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: unknown;
  placeholder?: string;
  onChange: (val: unknown) => void;
}) {
  const presets = ["{{user.name}}", "{{data.customers.0.name}}", "{{params.id}}"];
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm">{label}</Label>
        <BindingBadge value={value} />
      </div>
      <Input
        type="text"
        value={String(value ?? "")}
        placeholder={placeholder || "{{data.table.field}}"}
        onChange={(e) => onChange(e.target.value)}
        className={cn("font-mono text-xs", isBindingValue(value) && "border-primary/50 bg-primary/5")}
      />
      <p className="text-[10px] text-muted-foreground">{BINDING_HINT}</p>
      <div className="flex flex-wrap gap-1">
        {presets.map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => onChange(preset)}
            className="rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {preset}
          </button>
        ))}
      </div>
    </div>
  );
}

function PropField({
  label,
  type,
  value,
  options,
  placeholder,
  onChange,
}: {
  label: string;
  type: string;
  value: unknown;
  options?: unknown[];
  placeholder?: string;
  onChange: (val: unknown) => void;
}) {
  if (type === "boolean") {
    return (
      <div className="flex items-center justify-between">
        <Label className="text-sm">{label}</Label>
        <Switch checked={Boolean(value)} onCheckedChange={onChange} />
      </div>
    );
  }

  if (type === "select" && options) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm">{label}</Label>
        <Select value={String(value ?? "")} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder={placeholder || "请选择"} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={String(opt)} value={String(opt)}>
                {String(opt)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-sm">{label}</Label>
          <BindingBadge value={value} />
        </div>
        <Textarea
          value={String(value ?? "")}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className={cn(isBindingValue(value) && "border-primary/50 bg-primary/5")}
        />
        {isBindingValue(value) && <p className="text-[10px] text-muted-foreground">{BINDING_HINT}</p>}
      </div>
    );
  }

  if (type === "dataBinding") {
    return <DataBindingField label={label} value={value} placeholder={placeholder} onChange={onChange} />;
  }

  if (type === "json") {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm">{label}</Label>
        <Textarea
          value={typeof value === "object" ? JSON.stringify(value ?? [], null, 2) : String(value ?? "")}
          placeholder={placeholder}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              onChange(e.target.value);
            }
          }}
          rows={4}
          className="font-mono text-xs"
        />
      </div>
    );
  }

  // text, number, color
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm">{label}</Label>
        <BindingBadge value={value} />
      </div>
      <Input
        type={type === "number" ? "number" : type === "color" ? "color" : "text"}
        value={String(value ?? "")}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={cn(type === "text" && isBindingValue(value) && "border-primary/50 bg-primary/5")}
      />
      {type === "text" && isBindingValue(value) && <p className="text-[10px] text-muted-foreground">{BINDING_HINT}</p>}
    </div>
  );
}

type ActionType = "navigate" | "openModal" | "submitForm";

const ACTION_LABELS: Record<ActionType, string> = {
  navigate: "跳转页面",
  openModal: "打开弹窗",
  submitForm: "提交父表单",
};

const ACTION_DEFAULTS: Record<ActionType, Record<string, unknown>> = {
  navigate: { path: "/" },
  openModal: { title: "提示", content: "" },
  submitForm: {},
};

function ActionEditor({
  action,
  onChange,
  onDelete,
}: {
  action: ActionConfig;
  onChange: (action: ActionConfig) => void;
  onDelete: () => void;
}) {
  const type = (action.type as ActionType) || "navigate";
  const config = action.config || {};

  const updateType = (newType: ActionType) => {
    onChange({ type: newType, config: ACTION_DEFAULTS[newType] });
  };

  const updateConfig = (patch: Record<string, unknown>) => {
    onChange({ type, config: { ...config, ...patch } });
  };

  return (
    <div className="space-y-2 rounded-md border p-2">
      <div className="flex items-center gap-2">
        <Select value={type} onValueChange={(val) => updateType(val as ActionType)}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(ACTION_LABELS) as ActionType[]).map((t) => (
              <SelectItem key={t} value={t}>
                {ACTION_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onDelete}>
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>

      {type === "navigate" && (
        <div className="space-y-1">
          <Label className="text-xs">目标路由</Label>
          <Input
            className="h-8 text-xs"
            value={String(config.path ?? "")}
            placeholder="例如 /customers"
            onChange={(e) => updateConfig({ path: e.target.value })}
          />
        </div>
      )}

      {type === "openModal" && (
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-xs">弹窗标题</Label>
            <Input
              className="h-8 text-xs"
              value={String(config.title ?? "")}
              placeholder="例如 客户详情"
              onChange={(e) => updateConfig({ title: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">弹窗内容</Label>
            <Textarea
              className="text-xs"
              rows={3}
              value={String(config.content ?? "")}
              placeholder="支持 {{data.field}} 绑定"
              onChange={(e) => updateConfig({ content: e.target.value })}
            />
          </div>
        </div>
      )}

      {type === "submitForm" && (
        <p className="text-xs text-muted-foreground">点击后将触发最近的父表单提交。</p>
      )}
    </div>
  );
}

function EventBindingEditor({
  eventSchema,
  binding,
  onChange,
}: {
  eventSchema: EventSchema;
  binding?: EventBinding;
  onChange: (binding: EventBinding | undefined) => void;
}) {
  const actions = binding?.actions || [];

  const updateActions = (newActions: ActionConfig[]) => {
    if (newActions.length === 0) {
      onChange(undefined);
      return;
    }
    onChange({ event: eventSchema.event, actions: newActions });
  };

  const addAction = () => {
    updateActions([...actions, { type: "navigate", config: { path: "/" } }]);
  };

  const updateAction = (index: number, action: ActionConfig) => {
    const next = [...actions];
    next[index] = action;
    updateActions(next);
  };

  const deleteAction = (index: number) => {
    const next = actions.filter((_, i) => i !== index);
    updateActions(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm">
          {eventSchema.label} <span className="text-xs text-muted-foreground">({eventSchema.event})</span>
        </Label>
        <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={addAction}>
          <Plus className="h-3 w-3" />
          添加动作
        </Button>
      </div>
      {actions.length === 0 ? (
        <p className="text-xs text-muted-foreground">未配置动作</p>
      ) : (
        <div className="space-y-2">
          {actions.map((action, idx) => (
            <ActionEditor
              key={idx}
              action={action}
              onChange={(a) => updateAction(idx, a)}
              onDelete={() => deleteAction(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 6.4 配置驱动属性控件：按 PropertyPaneControl.controlType 渲染。
 * derived（派生）/ meta（瞬态）属性带类型徽标，派生属性只读。
 */
function ConfigControl({
  control,
  value,
  eventSchema,
  binding,
  onValueChange,
  onEventChange,
}: {
  control: PropertyPaneControl;
  value: unknown;
  eventSchema?: EventSchema[];
  binding?: EventBinding;
  onValueChange: (val: unknown) => void;
  onEventChange?: (binding: EventBinding | undefined) => void;
}) {
  const isDerived = control.propertyType === "derived";
  const isMeta = control.propertyType === "meta";
  const typeBadge = isDerived ? "派生" : isMeta ? "瞬态" : null;

  const renderHeader = (extra?: React.ReactNode) => (
    <div className="flex items-center justify-between">
      <Label className="text-sm">{control.label}</Label>
      <div className="flex items-center gap-1">
        {typeBadge && (
          <Badge variant="secondary" className="text-[10px]">
            {typeBadge}
          </Badge>
        )}
        {!isDerived && <BindingBadge value={value} />}
        {extra}
      </div>
    </div>
  );

  const renderHelp = () =>
    control.helpText ? (
      <p className="text-[10px] text-muted-foreground">{control.helpText}</p>
    ) : null;

  switch (control.controlType) {
    case "SWITCH":
      return (
        <div className="flex items-center justify-between">
          <Label className="text-sm">{control.label}</Label>
          <Switch
            checked={Boolean(value ?? control.defaultValue ?? false)}
            disabled={isDerived}
            onCheckedChange={onValueChange}
          />
        </div>
      );

    case "TEXT_AREA":
      return (
        <div className="space-y-1.5">
          {renderHeader()}
          <Textarea
            value={String(value ?? control.defaultValue ?? "")}
            placeholder={control.placeholder}
            onChange={(e) => onValueChange(e.target.value)}
            rows={3}
            disabled={isDerived}
            className={cn(isBindingValue(value) && "border-primary/50 bg-primary/5")}
          />
          {renderHelp()}
        </div>
      );

    case "JSON_INPUT":
      return (
        <div className="space-y-1.5">
          <Label className="text-sm">{control.label}</Label>
          <Textarea
            value={
              typeof value === "object"
                ? JSON.stringify(value ?? [], null, 2)
                : String(value ?? control.defaultValue ?? "")
            }
            placeholder={control.placeholder}
            onChange={(e) => {
              try {
                onValueChange(JSON.parse(e.target.value));
              } catch {
                onValueChange(e.target.value);
              }
            }}
            rows={4}
            disabled={isDerived}
            className="font-mono text-xs"
          />
          {renderHelp()}
        </div>
      );

    case "DATA_BINDING":
      return (
        <div className="space-y-1.5">
          {renderHeader()}
          <Input
            type="text"
            value={String(value ?? control.defaultValue ?? "")}
            placeholder={control.placeholder || "{{data.table.field}}"}
            onChange={(e) => onValueChange(e.target.value)}
            disabled={isDerived}
            className={cn("font-mono text-xs", isBindingValue(value) && "border-primary/50 bg-primary/5")}
          />
          {isBindingValue(value) && <p className="text-[10px] text-muted-foreground">{BINDING_HINT}</p>}
          {renderHelp()}
        </div>
      );

    case "EVENT_BINDER": {
      const schema = eventSchema?.find((s) => s.event === control.propertyName);
      if (!schema || !onEventChange) return null;
      return (
        <EventBindingEditor
          eventSchema={schema}
          binding={binding}
          onChange={onEventChange}
        />
      );
    }

    case "DROPDOWN": {
      const options = (control.options as unknown[]) || [];
      return (
        <div className="space-y-1.5">
          <Label className="text-sm">{control.label}</Label>
          <Select
            value={String(value ?? control.defaultValue ?? "")}
            onValueChange={onValueChange}
            disabled={isDerived}
          >
            <SelectTrigger>
              <SelectValue placeholder={control.placeholder || "请选择"} />
            </SelectTrigger>
            <SelectContent>
              {options.map((opt) => (
                <SelectItem key={String(opt)} value={String(opt)}>
                  {String(opt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {renderHelp()}
        </div>
      );
    }

    // INPUT_TEXT / INPUT_NUMBER / COLOR_PICKER
    default:
      return (
        <div className="space-y-1.5">
          {renderHeader()}
          <Input
            type={
              control.controlType === "INPUT_NUMBER"
                ? "number"
                : control.controlType === "COLOR_PICKER"
                  ? "color"
                  : "text"
            }
            value={String(value ?? control.defaultValue ?? "")}
            placeholder={control.placeholder}
            onChange={(e) => onValueChange(e.target.value)}
            disabled={isDerived}
            className={cn(
              control.controlType === "INPUT_TEXT" &&
                isBindingValue(value) &&
                "border-primary/50 bg-primary/5",
            )}
          />
          {control.controlType === "INPUT_TEXT" && isBindingValue(value) && (
            <p className="text-[10px] text-muted-foreground">{BINDING_HINT}</p>
          )}
          {renderHelp()}
        </div>
      );
  }
}

export function PropertyPanel() {
  const { selectedNodeId, findNodePath, updateNodeProps, updateNodeStyle, updateNodeEvents } = useBuilderStore();
  const [filter, setFilter] = React.useState("");
  const path = selectedNodeId ? findNodePath(selectedNodeId) : null;

  if (!path) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        在画布中选中一个组件以编辑属性。
      </div>
    );
  }

  const { node } = path;
  const material = getMaterial(node.type);
  const props = node.props || {};
  const style = node.style || {};
  const events = node.events || [];
  const eventSchemaList = material?.eventSchema || [];

  const term = filter.trim().toLowerCase();
  const matches = (label: string) => !term || label.toLowerCase().includes(term);

  const handlePropChange = (name: string, value: unknown) => {
    updateNodeProps(node.id, { [name]: value });
  };

  const handleStyleChange = (name: string, value: unknown) => {
    updateNodeStyle(node.id, { [name]: value });
  };

  const handleEventChange = (eventName: string, binding: EventBinding | undefined) => {
    const next = events.filter((e) => e.event !== eventName);
    if (binding) {
      next.push(binding);
    }
    updateNodeEvents(node.id, next);
  };

  const visibleProps = material?.propSchema.filter((f) => matches(f.label) || matches(f.name)) ?? [];
  const visibleEvents = eventSchemaList.filter((s) => matches(s.label) || matches(s.event));
  const styleFields = [
    { name: "padding", label: "内边距", type: "text" },
    { name: "margin", label: "外边距", type: "text" },
    { name: "background", label: "背景色", type: "color" },
    { name: "color", label: "文字颜色", type: "color" },
    { name: "borderRadius", label: "圆角", type: "text" },
    { name: "className", label: "自定义类名", type: "text" },
  ].filter((s) => matches(s.label) || matches(s.name));

  // 6.4 配置驱动：有 propertyPaneConfig 时按 section 渲染，否则回退 propSchema 平铺
  const paneConfig = material?.propertyPaneConfig;
  const useConfigDriven = Boolean(paneConfig && paneConfig.length > 0);
  const visibleSections = useConfigDriven ? filterPropertyPaneConfig(paneConfig, filter) : [];
  const showLegacyEvents = !useConfigDriven || !hasEventBinder(paneConfig);

  return (
    <div className="space-y-5 p-4">
      <div>
        <h3 className="text-sm font-semibold">{material?.label || node.type}</h3>
        <p className="text-xs text-muted-foreground">{node.id.slice(0, 8)}</p>
      </div>

      <div className="relative">
        <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="搜索属性 / 事件 / 样式"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-8 pl-7 text-xs"
        />
      </div>

      {useConfigDriven &&
        visibleSections.map((section) => (
          <div key={section.sectionName} className="space-y-3">
            <h4 className="text-xs font-semibold uppercase text-muted-foreground">
              {section.sectionName}
            </h4>
            {section.children.map((control) => (
              <ConfigControl
                key={control.propertyName}
                control={control}
                value={props[control.propertyName] ?? control.defaultValue}
                eventSchema={material?.eventSchema}
                binding={events.find((e) => e.event === control.propertyName)}
                onValueChange={(val) => handlePropChange(control.propertyName, val)}
                onEventChange={(binding) => handleEventChange(control.propertyName, binding)}
              />
            ))}
          </div>
        ))}

      {!useConfigDriven && material && visibleProps.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase text-muted-foreground">属性</h4>
          {visibleProps.map((field) => (
            <PropField
              key={field.name}
              label={field.label}
              type={field.type}
              value={props[field.name] ?? field.defaultValue ?? ""}
              options={field.options}
              placeholder={field.placeholder}
              onChange={(val) => handlePropChange(field.name, val)}
            />
          ))}
        </div>
      )}

      {showLegacyEvents && visibleEvents.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase text-muted-foreground">事件</h4>
          {visibleEvents.map((schema) => (
            <EventBindingEditor
              key={schema.event}
              eventSchema={schema}
              binding={events.find((e) => e.event === schema.event)}
              onChange={(binding) => handleEventChange(schema.event, binding)}
            />
          ))}
        </div>
      )}

      {styleFields.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase text-muted-foreground">样式</h4>
          {styleFields.map((s) => (
            <PropField
              key={s.name}
              label={s.label}
              type={s.type}
              value={(style[s.name as keyof typeof style] ?? "") as unknown as string}
              onChange={(val) => handleStyleChange(s.name, val)}
            />
          ))}
        </div>
      )}

      <div className="space-y-2">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground">原始 JSON</h4>
        <Textarea
          value={JSON.stringify(node, null, 2)}
          readOnly
          rows={8}
          className="font-mono text-xs"
        />
      </div>
    </div>
  );
}
